import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Popconfirm,
  Typography,
  Alert,
  Tooltip,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { channelAPI, botAPI } from '../services/api';
import { ManagedChannel, BotConfig } from '../types';

const { Option } = Select;
const { Title, Text } = Typography;

const ChannelManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<ManagedChannel[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<number | undefined>();
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchChannels = async () => {
    setLoading(true);
    try {
      const response = await channelAPI.getAll(selectedBotId);
      setChannels(response.data);
    } catch (error: any) {
      message.error('获取频道列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchBots = async () => {
    try {
      const response = await botAPI.getAll();
      setBots(response.data);
    } catch (error: any) {
      message.error('获取机器人列表失败');
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  useEffect(() => {
    fetchChannels();
  }, [selectedBotId]);

  const handleAdd = async (values: any) => {
    try {
      await channelAPI.create(values);
      message.success('频道添加成功');
      setAddModalVisible(false);
      form.resetFields();
      fetchChannels();
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || '添加失败';
      message.error(errorMsg);
    }
  };

  const handleRefresh = async (id: number) => {
    try {
      await channelAPI.refresh(id);
      message.success('频道信息已刷新');
      fetchChannels();
    } catch (error: any) {
      message.error(error.response?.data?.error || '刷新失败');
    }
  };

  const handleToggleStatus = async (record: ManagedChannel) => {
    const newStatus = record.status === 1 ? 0 : 1;
    try {
      await channelAPI.update(record.id, { status: newStatus });
      message.success(newStatus === 1 ? '频道已启用' : '频道已禁用');
      fetchChannels();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await channelAPI.delete(id);
      message.success('频道已删除');
      fetchChannels();
    } catch (error: any) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const getBotName = (botId: number) => {
    const bot = bots.find((b) => b.id === botId);
    return bot ? bot.bot_name : `Bot #${botId}`;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '频道信息',
      width: 250,
      render: (_: any, record: ManagedChannel) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.title || '未知频道'}</Text>
          {record.channel_username && (
            <Text type="secondary" copyable>
              @{record.channel_username}
            </Text>
          )}
          <Text type="secondary">ID: {record.channel_id}</Text>
        </Space>
      ),
    },
    {
      title: '所属机器人',
      dataIndex: 'bot_id',
      width: 150,
      render: (botId: number) => <Tag color="blue">{getBotName(botId)}</Tag>,
    },
    {
      title: '成员数',
      dataIndex: 'member_count',
      width: 100,
      render: (count: number) => (
        <Space>
          <TeamOutlined />
          {count || '-'}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: number) =>
        status === 1 ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            启用
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            禁用
          </Tag>
        ),
    },
    {
      title: '邀请链接',
      dataIndex: 'invite_link',
      width: 150,
      ellipsis: true,
      render: (link: string) =>
        link ? (
          <Typography.Paragraph
            copyable={{ text: link }}
            style={{ margin: 0, maxWidth: 120 }}
            ellipsis={{ rows: 1 }}
          >
            {link}
          </Typography.Paragraph>
        ) : (
          '-'
        ),
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      width: 220,
      fixed: 'right' as const,
      render: (_: any, record: ManagedChannel) => (
        <Space size="small">
          <Tooltip title="从 Telegram 刷新频道信息">
            <Button size="small" icon={<SyncOutlined />} onClick={() => handleRefresh(record.id)}>
              刷新
            </Button>
          </Tooltip>
          <Button
            size="small"
            onClick={() => handleToggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确定要删除此频道吗？"
            description="仅删除管理记录，不会影响频道本身"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>
          频道管理
        </Title>
        <Space>
          <Select
            style={{ width: 200 }}
            placeholder="筛选机器人"
            allowClear
            value={selectedBotId}
            onChange={setSelectedBotId}
          >
            {bots.map((bot) => (
              <Option key={bot.id} value={bot.id}>
                {bot.bot_name}
              </Option>
            ))}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchChannels}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
            添加频道
          </Button>
        </Space>
      </div>

      <Alert
        message="频道说明"
        description="机器人被添加为频道管理员时会自动注册到此列表。频道支持定时广播和自动回复功能，自动回复规则在「自动回复管理」中配置（将频道 ID 填入群组 ID 字段即可）。"
        type="info"
        showIcon
        closable
        style={{ marginBottom: 16 }}
      />

      <Card>
        <Table
          columns={columns}
          dataSource={channels}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 添加频道弹窗 */}
      <Modal
        title="手动添加频道"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="添加频道说明"
          description={
            <div>
              <p>1. 在 Telegram 频道设置中，将机器人添加为管理员（机器人会自动注册）</p>
              <p>2. 如果自动注册未生效，可在此手动添加</p>
              <p>3. 频道 ID 是负数（如 -1001234567890），可通过 @getidsbot 获取</p>
              <p>4. 机器人必须是频道管理员才能发送消息</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            label="选择机器人"
            name="botId"
            rules={[{ required: true, message: '请选择机器人' }]}
          >
            <Select placeholder="选择机器人">
              {bots
                .filter((bot) => bot.is_running)
                .map((bot) => (
                  <Option key={bot.id} value={bot.id}>
                    {bot.bot_name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="频道 ID"
            name="channelId"
            rules={[
              { required: true, message: '请输入频道 ID' },
              {
                validator: (_, value) => {
                  if (!value || value < 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('频道 ID 必须是负数'));
                },
              },
            ]}
            extra="频道 ID 是负数，例如：-1001234567890"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="-1001234567890"
              controls={false}
            />
          </Form.Item>

          <Form.Item label="频道用户名" name="channelUsername" extra="可选，例如：mychannel（不含 @）">
            <Input placeholder="mychannel" />
          </Form.Item>

          <Form.Item label="频道标题" name="title" extra="可选，会自动从 Telegram 获取">
            <Input placeholder="我的频道" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
              <Button onClick={() => setAddModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ChannelManagement;
